<!-- src/views/pages/hrm/attendance/performance/goal-tracking-table.vue -->
<template>
  <a-table
    class="table datatable thead-light"
    :columns="columns"
    :data-source="rows"
    :loading="loading"
    row-key="id"
    :row-selection="rowSelection"
  >
    <template #bodyCell="{ column, record }">
      <template v-if="column.key === 'goal_type'">
        {{ record?.goal_type?.goal_type || record?.goal?.goal_type || '-' }}
      </template>

      <template v-else-if="column.key === 'subject'">
        {{ record?.subject || '-' }}
      </template>

      <template v-else-if="column.key === 'target'">
        {{ record?.target_achievement || '-' }}
      </template>

      <template v-else-if="column.key === 'start'">
        {{ prettyDate(record?.start_date) }}
      </template>

      <template v-else-if="column.key === 'end'">
        {{ prettyDate(record?.end_date) }}
      </template>

      <template v-else-if="column.key === 'desc'">
        {{ record?.description || '-' }}
      </template>

      <template v-else-if="column.key === 'status'">
        <span class="badge d-inline-flex align-items-center badge-xs"
              :class="statusClass(record?.status)">
          <i class="ti ti-point-filled me-1"></i>{{ record?.status || '-' }}
        </span>
      </template>

      <template v-else-if="column.key === 'progress'">
        <div class="d-flex flex-column">
          <span class="fs-12 mb-1">
            Completed {{ progressPercent(record) }}%
          </span>
          <div class="progress" role="progressbar"
               :aria-valuenow="progressPercent(record)"
               aria-valuemin="0" aria-valuemax="100"
               style="width: 120px; height: 6px">
            <div class="progress-bar bg-primary"
                 :style="{ width: progressPercent(record) + '%' }">
            </div>
          </div>
        </div>
      </template>

      <template v-else-if="column.key === 'action'">
        <div class="action-icon d-inline-flex">
          <a href="javascript:void(0);" class="me-2" @click="$emit('edit', record)" v-can="'GoalTracking.Edit'">
            <i class="ti ti-edit"></i>
          </a>
          <a href="javascript:void(0);" @click="$emit('delete', record)" v-can="'GoalTracking.Delete'">
            <i class="ti ti-trash"></i>
          </a>
        </div>
      </template>
      
    </template>
  </a-table>
</template>

<script setup>
import { ref } from "vue";

const props = defineProps({
  rows: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
});
const emit = defineEmits(["edit", "delete", "bulk-delete"]);

const selectedRowKeys = ref([]);

const columns = [
  { title: "Goal Type", dataIndex: "goal_type", key: "goal_type" },
  { title: "Subject", dataIndex: "subject", key: "subject" },
  { title: "Target Achievement", dataIndex: "target_achievement", key: "target" },
  { title: "Start Date", dataIndex: "start_date", key: "start" },
  { title: "End Date", dataIndex: "end_date", key: "end" },
  { title: "Description", dataIndex: "description", key: "desc" },
  { title: "Status", dataIndex: "status", key: "status" },
  { title: "Progress", dataIndex: "progress", key: "progress" },
  { title: "", dataIndex: "action", key: "action" },
];

const rowSelection = {
  selectedRowKeys: selectedRowKeys.value,
  onChange: (keys) => { selectedRowKeys.value = keys; },
  onSelect: () => {},
  onSelectAll: () => {},
};

// ---- helpers
function clamp(n, min=0, max=100){ return Math.max(min, Math.min(max, Math.round(n))); }

function progressPercent(r) {
  // 1) explicit percent from API
  if (r?.progress_percent !== undefined && r?.progress_percent !== null) {
    return clamp(Number(r.progress_percent));
  }
  // 2) ratio completed / total from API
  if (r?.progress_completed !== undefined && r?.progress_total) {
    const pct = (Number(r.progress_completed) / Number(r.progress_total)) * 100;
    return clamp(pct);
  }
  // 3) date-based fallback (today relative to start/end)
  const s = r?.start_date ? new Date(r.start_date) : null;
  const e = r?.end_date ? new Date(r.end_date) : null;
  if (s && e && !isNaN(s) && !isNaN(e) && e > s) {
    const now = new Date();
    if (now <= s) return 0;
    if (now >= e) return 100;
    const pct = ((now - s) / (e - s)) * 100;
    return clamp(pct);
  }
  // default
  return 0;
}

function prettyDate(v) {
  if (!v) return "-";
  try { return new Date(v).toLocaleDateString(); } catch { return v; }
}

function statusClass(s) {
  const v = String(s || "").toLowerCase();
  return {
    "badge-success": v === "active",
    "badge-warning": v === "inactive",
    "badge-info": v === "ongoing",
    "badge-secondary": v === "scheduled",
    "badge-danger": v === "cancelled",
  };
}

// bulk delete on keyboard
window.addEventListener("keydown", (e) => {
  if (e.key === "Delete" && selectedRowKeys.value.length) {
    emit("bulk-delete", selectedRowKeys.value);
  }
});
</script>
